import { ArrayUtil, DbDisplayValue, StringUtil } from "@mcleod/core";
import { ChangeEvent, Checkbox, CheckboxProps, DecoratorType, DialogProps, Label, Panel } from "@mcleod/components";
import { MultipleValueSelector } from "./MultipleValueSelector";

export class LayoutUseSelector extends MultipleValueSelector {
    private _allowedUseGroups: Map<string, AllowedUseGroup>;

    override getDialogProps(): Partial<DialogProps> {
        return {
            ...super.getDialogProps(),
            height: 750,
            okDefault: false
        }
    }

    protected override getTitle(): string {
        return "Select Layout Uses";
    }

    protected getHeadingLabelText(): string {
        return "Specify how the layout can be used.";
    }

    protected discoverValidValues(): DbDisplayValue[] {
        const result: DbDisplayValue[] = [];
        for (const allowedUseGroup of this.allowedUseGroups.values()) {
            result.push(allowedUseGroup.dbDisplayValue);
        }
        return result;
    }

    protected markSelectedValues(allowedUses: any) {
        const uses: DecoratorType[] = allowedUses.uses;
        const defaultUse: DecoratorType = allowedUses.defaultUse;
        for (const allowedUseGroup of this.allowedUseGroups.values()) {
            allowedUseGroup.setStateFromValues(uses, defaultUse);
        }
    }

    private get allowedUseGroups(): Map<string, AllowedUseGroup> {
        if (this._allowedUseGroups == null) {
            const groups = [ this.crudGroup, this.reportGroup, this.taskGroup ];
            this._allowedUseGroups = new Map(groups.map(group => [group.id, group]));
        }
        return this._allowedUseGroups;
    }

    private get crudGroup(): AllowedUseGroup {
        const groupDbValue = "addUpdateSearch";
        const groupDisplayValue = "Add/Update/Search Operations";
        const decoratorTypes = [ DecoratorType.ADD, DecoratorType.UPDATE, DecoratorType.SEARCH ];
        const defaultDecoratorTypes = [ DecoratorType.ADD, DecoratorType.UPDATE ];
        const description = "Layouts that are used for add/update/search operations typically display data for a " + 
            "single record (and any of the record's child data).  It is not uncommon for a screen to be used for all " +
            "three actions.";
        return new AllowedUseGroup(groupDbValue, groupDisplayValue, decoratorTypes, description, defaultDecoratorTypes);
    }

    private get reportGroup(): AllowedUseGroup {
        const groupDbValue = DecoratorType.REPORT;
        const groupDisplayValue = "Report Generation";
        const decoratorTypes = [ DecoratorType.REPORT ];
        const description = "Layouts that are used to generate reports contain input fields that are used to specify " + 
            "information to allow for the report(s) to be run.";
        return new AllowedUseGroup(groupDbValue, groupDisplayValue, decoratorTypes, description);
    }

    private get taskGroup(): AllowedUseGroup {
        const groupDbValue = DecoratorType.TASK;
        const groupDisplayValue = "Task Processing";
        const decoratorTypes = [ DecoratorType.TASK ];
        const description = "Layouts that are used to complete tasks contain input fields that are used to specify " + 
            "information to allow for the task to be executed.  Task layouts typically offer a preview action (such " + 
            "as an edit list) an execute action (which actually performs the task).  The task typically returns " +
            "reports or some other form of output that displays the result of the task.";
        return new AllowedUseGroup(groupDbValue, groupDisplayValue, decoratorTypes, description);
    }

    protected addCheckboxes() {
        const panelCheckboxes = super.createPanelCheckboxes();
        this.checkboxes.forEach(checkbox => {
            checkbox.addChangeListener((event: ChangeEvent) => this.onGroupCheckboxChange(event));
            const allowedUseGroup = this.allowedUseGroups.get(checkbox.valueChecked);
            allowedUseGroup.groupCheckbox = checkbox;
            const groupPanel = allowedUseGroup.buildPanel();
            panelCheckboxes.add(groupPanel);
        });
        this.add(panelCheckboxes);
    }

    private onGroupCheckboxChange(event: ChangeEvent) {
        if (!event.userInitiatedChange) {
            return;
        }
        const updatedCheckbox = event.target as Checkbox;
        const selectedAllowedUseGroup =
            updatedCheckbox.checked ? this.allowedUseGroups.get(updatedCheckbox.valueChecked) : null;
        for (const allowedUseGroup of this.allowedUseGroups.values()) {
            const groupIsSelected = allowedUseGroup === selectedAllowedUseGroup;
            allowedUseGroup.updateCheckboxes(groupIsSelected);
        }
    }

    public override getResultValue(): any {
        const resultJson = this.getResultJson();
        return resultJson != null ? JSON.stringify(resultJson) : null;
    }

    private getResultJson(): any {
        const result = {
            uses: [],
            defaultUse: null
        };
        this.allowedUseGroups.forEach(allowedUseGroup => {
            result.uses.push(...allowedUseGroup.allowedDecoratorTypes);
            const defaultDecoratorType = allowedUseGroup.defaultDecoratorType;
            if (defaultDecoratorType != null) {
                result.defaultUse = defaultDecoratorType;
            }
        });
        return !ArrayUtil.isEmptyArray(result.uses) ? result : null;
    }
}

class AllowedUseGroup {
    dbDisplayValue: DbDisplayValue;
    private _decoratorTypes: DecoratorType[];
    private defaultDecoratorTypes: DecoratorType[];
    private _descr: string;
    groupCheckbox: Checkbox;
    checkboxesByDecoratorType: Map<DecoratorType, Checkbox> = new Map();
    defaultCheckboxesByDecoratorType: Map<DecoratorType, Checkbox> = new Map();

    constructor(dbValue: string, displayValue: string, decoratorTypes: DecoratorType[], descr: string,
        defaultDecoratorTypes: DecoratorType[] = decoratorTypes) {
        this.dbDisplayValue = new DbDisplayValue(dbValue, displayValue);
        this._decoratorTypes = decoratorTypes;
        this.defaultDecoratorTypes = defaultDecoratorTypes;
        this._descr = descr;
    }

    get decoratorTypes(): DecoratorType[] {
        return this._decoratorTypes ?? [];
    }

    get descr(): string {
        return this._descr ?? "";
    }

    get allowedDecoratorTypes(): DecoratorType[] {
        const result: DecoratorType[] = [];
        this.checkboxesByDecoratorType.forEach((checkbox: Checkbox, decoratorType: DecoratorType) => {
            if (checkbox.checked === true) {
                result.push(decoratorType);
            }
        });
        return result;
    }

    get defaultDecoratorType(): DecoratorType {
        let result: DecoratorType = null;
        this.defaultCheckboxesByDecoratorType.forEach((defaultCheckbox: Checkbox, decoratorType: DecoratorType) => {
            if (defaultCheckbox.checked === true && this.defaultDecoratorTypes.includes(decoratorType)) {
                result = decoratorType;
            }
        });
        return result;
    }

    get id(): string {
        return this.dbDisplayValue.value;
    }

    private getInnerComponentId(value: string): string {
        return `group-${this.id}-${value}`;
    }

    buildPanel(): Panel {
        const groupPanel = Panel.createNoMarginNoPaddingPanel({
            id: this.getInnerComponentId("panel"),
            fillRow: true,
            marginTop: 15,
            rowBreak: true,
            scrollY: false
        });
        const panelDecoratorTypes = Panel.createNoMarginNoPaddingPanel({
            id: this.getInnerComponentId("panelDecoratorTypes"),
            marginLeft: 25,
            minWidth: 150,
            rowBreak: false,
            scrollY: false
        });
        const panelDecoratorTypeDefaults = Panel.createNoMarginNoPaddingPanel({
            id: this.getInnerComponentId("panelDecoratorTypeDefaults"),
            marginLeft: 10,
            scrollY: false
        });
        this.decoratorTypes.forEach((decoratorType: DecoratorType) => {
            const checkboxForDecoratorType = this.createDecoratorTypeCheckbox(decoratorType);
            panelDecoratorTypes.add(checkboxForDecoratorType);
            const defaultCheckboxForDecoratorType = this.createDecoratorTypeDefaultCheckbox(decoratorType);
            panelDecoratorTypeDefaults.add(defaultCheckboxForDecoratorType);
        });
        this.groupCheckbox.id = this.getInnerComponentId("checkbox"),
        this.groupCheckbox.rowBreak = true;
        const labelDescr = this.createLabel(Number(panelDecoratorTypes.marginLeft) + 7);
        groupPanel.add(this.groupCheckbox, labelDescr, panelDecoratorTypes, panelDecoratorTypeDefaults);
        return groupPanel;
    }

    private createLabel(marginLeft: number): Label {
        return new Label({
            id: this.getInnerComponentId("label"),
            marginLeft,
            text: this.descr
        });
    }

    private createDecoratorTypeCheckbox(decoratorType: DecoratorType): Checkbox {
        const checkbox = new Checkbox({
            caption: `${StringUtil.capitalize(decoratorType)}`,
            enabled: false,
            id: this.getInnerComponentId(`checkbox-${decoratorType}`),
            valueChecked: decoratorType,
            valueUnchecked: null
        });
        checkbox.allowIndeterminate = false;
        checkbox.addChangeListener((event: ChangeEvent) => this.onDecoratorTypeCheckboxChange(event));
        this.checkboxesByDecoratorType.set(decoratorType, checkbox);
        return checkbox;
    }

    private onDecoratorTypeCheckboxChange(event: ChangeEvent) {
        if (!event.userInitiatedChange) {
            return;
        }
        const updatedCheckbox = event.target as Checkbox;
        const defaultCheckbox = this.defaultCheckboxesByDecoratorType.get(updatedCheckbox.valueChecked);
        if (updatedCheckbox.checked) {
            defaultCheckbox.enabled = true;
        } else {
            defaultCheckbox.checked = false;
            defaultCheckbox.enabled = false;
        }
    }

    private createDecoratorTypeDefaultCheckbox(decoratorType: DecoratorType): Checkbox {
        const defaultCheckbox = new Checkbox({
            caption: "Use as Default",
            enabled: false,
            id: this.getInnerComponentId(`checkbox-${decoratorType}-default`),
            valueChecked: decoratorType,
            valueUnchecked: null,
            preventCollapse: true
        });
        defaultCheckbox.allowIndeterminate = false;
        defaultCheckbox.addChangeListener((event: ChangeEvent) => this.onDefaultCheckboxChange(event));
        this.defaultCheckboxesByDecoratorType.set(decoratorType, defaultCheckbox);
        // Hide default checkboxes that aren't valid, but preventCollapse so that spacing stays correct
        defaultCheckbox.visible = this.defaultDecoratorTypes.includes(decoratorType)
        return defaultCheckbox;
    }

    private onDefaultCheckboxChange(event: ChangeEvent) {
        if (!event.userInitiatedChange) {
            return;
        }
        const updatedCheckbox = event.target as Checkbox;
        if (!updatedCheckbox.checked) {
            return;
        }
        this.updateDefaultCheckboxes((checkbox: Checkbox) => { return { checked: updatedCheckbox === checkbox }});
    }

    private get numberDecoratorTypesInGroup(): number {
        return ArrayUtil.getLength(this.decoratorTypes);
    }

    updateCheckboxes(groupIsSelected: boolean) {
        const decoratorTypeCheckboxProps: Partial<CheckboxProps> = {};
        const defaultCheckboxProps: Partial<CheckboxProps> = {};
        if (groupIsSelected) {
            const oneDecoratorTypeInGroup = this.numberDecoratorTypesInGroup === 1;
            decoratorTypeCheckboxProps.checked = oneDecoratorTypeInGroup;
            decoratorTypeCheckboxProps.enabled = !oneDecoratorTypeInGroup;
            defaultCheckboxProps.checked = oneDecoratorTypeInGroup;
            defaultCheckboxProps.enabled = false;
        } else {
            this.groupCheckbox.checked = false;
            decoratorTypeCheckboxProps.checked = false;
            decoratorTypeCheckboxProps.enabled = false;
            defaultCheckboxProps.checked = false;
            defaultCheckboxProps.enabled = false;
        }
        this.updateDecoratorTypeCheckboxes(decoratorTypeCheckboxProps);
        this.updateDefaultCheckboxes(defaultCheckboxProps);
    }

    private updateDecoratorTypeCheckboxes(props: Partial<CheckboxProps>) {
        this.updateCheckboxesInMap(this.checkboxesByDecoratorType, props);
    }

    private updateDefaultCheckboxes(props: Partial<CheckboxProps>|((checkbox: Checkbox) => Partial<CheckboxProps>)) {
        this.updateCheckboxesInMap(this.defaultCheckboxesByDecoratorType, props);
    }

    private updateCheckboxesInMap(checkboxMap: Map<DecoratorType, Checkbox>,
        props: Partial<CheckboxProps>|((checkbox: Checkbox) => Partial<CheckboxProps>)) {
        for (const checkbox of checkboxMap.values()) {
            const propsToUse = (typeof props === "function") ? props(checkbox) : props;
            checkbox.setProps(propsToUse);
        }
    }

    setStateFromValues(uses: DecoratorType[], defaultUse: DecoratorType) {
        let activatingGroup = false;
        this.checkboxesByDecoratorType.forEach((checkbox: Checkbox, decoratorType: DecoratorType) => {
            if (uses?.includes(decoratorType)) {
                activatingGroup = true;
                checkbox.checked = true;
                this.enableDefaultCheckbox(decoratorType);
            }
        });
        const defaultCheckbox = this.defaultCheckboxesByDecoratorType.get(defaultUse);
        if (defaultCheckbox != null) {
            activatingGroup = true;
            if (this.defaultDecoratorTypes.includes(defaultUse)) {
                defaultCheckbox.checked = true;
            }
        }
        if (activatingGroup) {
            this.groupCheckbox.checked = true;
            this.updateDecoratorTypeCheckboxes({ enabled: true });
            this.checkboxesByDecoratorType.forEach((checkbox: Checkbox, decoratorType: DecoratorType) => {
                if (checkbox.checked) {
                    this.enableDefaultCheckbox(decoratorType);
                }
            });
            if (this.numberDecoratorTypesInGroup === 1) {
                this.updateDecoratorTypeCheckboxes({ enabled: false });
                this.updateDefaultCheckboxes({ enabled: false });
            }
            
        }
    }

    private enableDefaultCheckbox(decoratorType: DecoratorType) {
        const defaultCheckbox = this.defaultCheckboxesByDecoratorType.get(decoratorType);
        if (defaultCheckbox != null) {
            defaultCheckbox.enabled = true;
        }
    }
}
